package com.ijoin.ui.chat

import com.ijoin.data.model.ChatMessage
import com.ijoin.data.model.ChatThread
import com.ijoin.data.model.ChatUserSummary
import com.ijoin.data.repository.ChatRepository
import com.ijoin.i18n.Strings
import com.ijoin.i18n.globalStrings
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException

data class ChatListUiState(
    val isLoading: Boolean = true,
    val chats: List<ChatThread> = emptyList(),
    val error: String? = null,
    val isSearching: Boolean = false,
    val searchResults: List<ChatUserSummary> = emptyList(),
    val searchError: String? = null,
)

class ChatListViewModel(
    private val repository: ChatRepository = ChatRepository(),
) {
    private val viewModelScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private var searchJob: Job? = null

    private val _uiState = MutableStateFlow(ChatListUiState())
    val uiState: StateFlow<ChatListUiState> = _uiState.asStateFlow()

    private val strings: Strings
        get() = globalStrings()

    init {
        refresh()
    }

    fun refresh() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            val result = repository.getChats()
            if (result.isSuccess) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        chats = result.getOrNull().orEmpty(),
                        error = null,
                    )
                }
            } else {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = result.exceptionOrNull()?.message ?: strings.chatLoadError,
                    )
                }
            }
        }
    }

    fun searchUsers(query: String) {
        val trimmed = query.trim()
        if (trimmed.isEmpty()) {
            clearSearch()
            return
        }

        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            _uiState.update { it.copy(isSearching = true, searchError = null) }
            val result = repository.searchUsers(trimmed)
            if (result.isSuccess) {
                _uiState.update {
                    it.copy(
                        isSearching = false,
                        searchResults = result.getOrNull().orEmpty(),
                        searchError = null,
                    )
                }
            } else {
                _uiState.update {
                    it.copy(
                        isSearching = false,
                        searchError = result.exceptionOrNull()?.message ?: strings.chatSearchError,
                    )
                }
            }
        }
    }

    fun clearSearch() {
        searchJob?.cancel()
        _uiState.update {
            it.copy(
                isSearching = false,
                searchResults = emptyList(),
                searchError = null,
            )
        }
    }
}

data class ChatUiState(
    val thread: ChatThread? = null,
    val messages: List<ChatMessage> = emptyList(),
    val isLoading: Boolean = true,
    val isSending: Boolean = false,
    val error: String? = null,
)

class ChatViewModel(
    initialThread: ChatThread?,
    private val otherUserId: String?,
    private val repository: ChatRepository = ChatRepository(),
) {
    private val viewModelScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private var streamJob: Job? = null
    private var streamingChatId: String? = null

    private val _uiState = MutableStateFlow(ChatUiState(thread = initialThread, isLoading = true))
    val uiState: StateFlow<ChatUiState> = _uiState.asStateFlow()
    private val strings: Strings
        get() = globalStrings()

    init {
        viewModelScope.launch {
            if (initialThread != null) {
                // Mark as read best-effort and then load messages
                viewModelScope.launch { repository.markThreadRead(initialThread.id) }
                loadMessages(initialThread.id)
            } else {
                openChat()
            }
        }
    }

    fun reload() {
        viewModelScope.launch {
            val thread = ensureThread()
            if (thread != null) {
                // Mark as read best-effort and then load messages
                viewModelScope.launch { repository.markThreadRead(thread.id) }
                loadMessages(thread.id)
            }
        }
    }

    fun sendMessage(content: String) {
        if (content.isBlank()) return

        viewModelScope.launch {
            val thread = ensureThread()
            if (thread == null) {
                _uiState.update {
                    it.copy(error = strings.chatStartError)
                }
                return@launch
            }

            _uiState.update { it.copy(isSending = true, error = null) }
            val result = repository.sendMessage(thread.id, content.trim())
            if (result.isSuccess) {
                val message = result.getOrNull()
                if (message != null) {
                    _uiState.update { state ->
                        val alreadyExists = state.messages.any { it.id == message.id }
                        state.copy(
                            isSending = false,
                            messages = if (alreadyExists) state.messages else state.messages + message,
                            error = null,
                        )
                    }
                } else {
                    _uiState.update { it.copy(isSending = false) }
                }
            } else {
                val error = result.exceptionOrNull()
                if (error is CancellationException) {
                    _uiState.update { it.copy(isSending = false) }
                    return@launch
                }
                _uiState.update {
                    it.copy(
                        isSending = false,
                        error = error?.message ?: strings.chatSendError,
                    )
                }
            }
        }
    }

    private suspend fun loadMessages(chatId: String) {
        _uiState.update { it.copy(isLoading = true, error = null) }
        val result = repository.getMessages(chatId)
        if (result.isSuccess) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    messages = result.getOrNull().orEmpty(),
                    error = null,
                )
            }
            startMessageStream(chatId)
        } else {
            val error = result.exceptionOrNull()
            if (error is CancellationException) {
                _uiState.update { it.copy(isLoading = false) }
                return
            }
            _uiState.update {
                it.copy(
                    isLoading = false,
                    error = error?.message ?: strings.chatLoadError,
                )
            }
        }
    }

    private suspend fun openChat() {
        val targetUserId = otherUserId ?: return
        _uiState.update { it.copy(isLoading = true, error = null) }
        val result = repository.openChatWithUser(targetUserId)
        if (result.isSuccess) {
            val thread = result.getOrNull()
            _uiState.update {
                it.copy(
                    thread = thread,
                    error = null,
                )
            }
            if (thread != null) {
                // Mark as read best-effort and then load messages
                viewModelScope.launch { repository.markThreadRead(thread.id) }
                loadMessages(thread.id)
            } else {
                _uiState.update { it.copy(isLoading = false) }
            }
        } else {
            val error = result.exceptionOrNull()
            if (error is CancellationException) {
                _uiState.update { it.copy(isLoading = false) }
                return
            }
            _uiState.update {
                it.copy(
                    isLoading = false,
                    error = error?.message ?: strings.chatOpenError,
                )
            }
        }
    }

    private suspend fun ensureThread(): ChatThread? {
        val existing = _uiState.value.thread
        if (existing != null) {
            return existing
        }
        openChat()
        return _uiState.value.thread
    }

    private fun startMessageStream(chatId: String) {
        if (streamingChatId == chatId && streamJob?.isActive == true) {
            return
        }
        streamJob?.cancel()
        streamingChatId = chatId
        streamJob = viewModelScope.launch {
            repository.streamMessages(chatId).collect { incoming ->
                _uiState.update { state ->
                    if (state.messages.any { it.id == incoming.id }) {
                        state
                    } else {
                        state.copy(messages = state.messages + incoming)
                    }
                }
            }
        }
    }

    fun clear() {
        streamJob?.cancel()
        streamJob = null
        streamingChatId = null
        // Note: We don't cancel viewModelScope here to allow graceful shutdown
        // The scope will be garbage collected with the ViewModel
    }
}
